import { Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { confirmResetPassword, resetPassword } from '@aws-amplify/auth';
import { passwordValidator } from '@railmybox/shared/util';
import { AuthQuery, AuthStoreService } from '../../store/auth';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'rmb-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false,
})
export class ResetPasswordComponent {
  private router = inject(Router);
  private formBuilder = inject(FormBuilder);
  private authQuery = inject(AuthQuery);
  private authStoreService = inject(AuthStoreService);
  private translocoService = inject(TranslocoService);

  readonly TRANSLOCO_PREFIX = 'auth.resetPassword.form';

  resetPasswordForm: FormGroup;
  errorMessage: string;
  counter: string;
  passwordSubmitted = false;
  showCodeResent = false;
  formError = false;
  disableActions = signal(false);

  constructor() {
    this.resetPasswordForm = this.getPasswordResetForm();
  }

  /**
   * resends verification code
   */
  async resendCode(): Promise<void> {
    if (this.disableActions()) {
      return;
    }

    this.errorMessage = '';
    const username = this.authQuery.getUserEmail();
    this.disableActions.set(true);
    try {
      resetPassword({ username }).then(() => {
        this.showCodeResent = true;
        this.disableActions.set(false);
      });
    } catch (err) {
      this.errorMessage = this.translocoService.translate('auth.errors.' + err.name);
      this.disableActions.set(false);
    }
  }

  /**
   * Checks if two form controls have the same value
   */
  matching(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ match: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  /**
   * sets new password
   */
  async onSubmit(): Promise<void> {
    if (this.resetPasswordForm.invalid) {
      return;
    }

    this.errorMessage = '';
    const { code, password } = this.resetPasswordForm.value;

    this.disableActions.set(true);
    const username = this.authQuery.getUserEmail();
    confirmResetPassword({ username: username, newPassword: password, confirmationCode: code.trim() })
      .then(() => {
        this.passwordSubmitted = true;
        this.authStoreService.updateUserCredentials({ password: password });
        this.authStoreService.updateUIState({ destination: 'login' });

        this.router.navigate(['/auth', 'login']);
        this.disableActions.set(false);
      })
      .catch((err) => {
        this.errorMessage = this.translocoService.translate('auth.errors.' + err.name);
        this.formError = true;
        this.disableActions.set(false);
      });
  }

  /**
   * retrieves the reset password form
   * @private
   */
  private getPasswordResetForm(): FormGroup {
    return this.formBuilder.group(
      {
        code: ['', [Validators.required]],
        password: ['', [passwordValidator]],
        confirmPassword: ['', [Validators.required]],
      },
      {
        validator: this.matching('password', 'confirmPassword'),
      }
    );
  }
}
